import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import { INR, fmtDate, todayIso } from "@/lib/format";
import { Plus, Trash2, FileText, RefreshCw, Search, Printer, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import EmptyState from "@/components/EmptyState";

const SERVICES = ["Meta Ads", "Google Ads", "Social Media Management", "Website Development", "SEO", "Telecalling", "E-commerce Management", "Graphic Designing", "Video Editing", "Content Creation", "Marketing Consulting", "Branding", "Other"];
const STATUSES = ["Draft", "Sent", "Viewed", "Negotiation", "Accepted", "Rejected", "Expired"];

const emptyItem = { service_name: "", description: "", quantity: 1, original_price: 0, discount_percent: 0, negotiated_price: 0, tax_percent: 18 };

const lineTotal = (it) => {
  const base = Number(it.negotiated_price || it.original_price || 0) * Number(it.quantity || 1);
  return base + base * Number(it.tax_percent || 0) / 100;
};

export default function Quotations() {
  const nav = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ client_id: "", date: todayIso(), valid_until: "", items: [{ ...emptyItem }], terms: "50% advance, 50% on delivery.", notes: "", status: "Draft" });
  const [detail, setDetail] = useState(null);
  // Filters
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const [clientF, setClientF] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/quotations"); setQuotes(r.data); }
    catch (_) { toast.error("Could not load quotations"); }
    setLoading(false);
  };
  useEffect(() => { load(); api.get("/clients").then((r) => setClients(r.data)); }, []);

  const subtotal = form.items.reduce((s, it) => s + (Number(it.negotiated_price || it.original_price || 0)) * Number(it.quantity || 1), 0);
  const taxTotal = form.items.reduce((s, it) => s + (Number(it.negotiated_price || it.original_price || 0)) * Number(it.quantity || 1) * Number(it.tax_percent || 0) / 100, 0);
  const finalTotal = subtotal + taxTotal;

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    return quotes.filter((quo) => {
      if (statusF && quo.status !== statusF) return false;
      if (clientF && quo.client_id !== clientF) return false;
      if (dateFrom && (quo.date || "") < dateFrom) return false;
      if (dateTo && (quo.date || "") > dateTo) return false;
      if (qq) {
        const client = clients.find((c) => c.id === quo.client_id);
        const hay = `${quo.quote_number || ""} ${client?.company_name || ""}`.toLowerCase();
        if (!hay.includes(qq)) return false;
      }
      return true;
    });
  }, [quotes, q, statusF, clientF, dateFrom, dateTo, clients]);

  const save = async () => {
    if (!form.client_id) { toast.error("Choose a client"); return; }
    if (!form.items.length) { toast.error("Add at least one item"); return; }
    try { await api.post("/quotations", form); toast.success("Quotation created"); setOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const changeStatus = async (quo, status) => {
    try { await api.patch(`/quotations/${quo.id}/status`, { status }); load(); if (detail?.id === quo.id) setDetail({ ...detail, status }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (quo) => {
    try { await api.post(`/quotations/${quo.id}/convert`); toast.success("Converted to services + project"); load(); setDetail(null); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const clearFilters = () => { setQ(""); setStatusF(""); setClientF(""); setDateFrom(""); setDateTo(""); };
  const hasFilters = q || statusF || clientF || dateFrom || dateTo;

  return (
    <div className="space-y-6" data-testid="quotations-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Sales</div>
          <h1 className="text-4xl font-black tracking-tight">Quotations</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} of {quotes.length} · click a row to view or print.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="quotes-refresh-btn" variant="outline" size="icon" onClick={load} title="Refresh"><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild><Button data-testid="new-quote-btn" className="bg-primary hover:bg-primary/90"><Plus size={14} className="mr-1" />New Quotation</Button></SheetTrigger>
            <SheetContent className="sm:max-w-3xl overflow-y-auto">
              <SheetHeader><SheetTitle>New Quotation</SheetTitle></SheetHeader>
              <div className="mt-5 space-y-4">
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="font-mono text-[10px] uppercase">Client *</Label>
                    <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                      <SelectTrigger data-testid="quote-form-client"><SelectValue placeholder="Choose client" /></SelectTrigger>
                      <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label className="font-mono text-[10px] uppercase">Valid until</Label><Input type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} /></div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase text-muted-foreground mb-2">Line items</div>
                  <div className="space-y-3">
                    {form.items.map((it, idx) => (
                      <div key={idx} className="border border-border rounded-md p-3 bg-muted/30 space-y-3" data-testid={`quote-line-${idx}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 space-y-2">
                            <Label className="font-mono text-[10px] uppercase">Service *</Label>
                            <Select value={it.service_name} onValueChange={(v) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, service_name: v } : x) })}>
                              <SelectTrigger data-testid={`quote-item-${idx}-service`}><SelectValue placeholder="Choose service" /></SelectTrigger>
                              <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                          {form.items.length > 1 && (
                            <Button variant="ghost" size="icon" className="mt-6" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} data-testid={`quote-item-${idx}-remove`}><Trash2 size={14} /></Button>
                          )}
                        </div>
                        <div>
                          <Label className="font-mono text-[10px] uppercase">Description</Label>
                          <Input placeholder="What's included?" value={it.description} onChange={(e) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x) })} />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          <div>
                            <Label className="font-mono text-[10px] uppercase">Quantity</Label>
                            <Input type="number" min="1" value={it.quantity} onChange={(e) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, quantity: parseFloat(e.target.value || 0) } : x) })} />
                          </div>
                          <div>
                            <Label className="font-mono text-[10px] uppercase">Original price (₹)</Label>
                            <Input type="number" min="0" value={it.original_price} onChange={(e) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, original_price: parseFloat(e.target.value || 0) } : x) })} />
                          </div>
                          <div>
                            <Label className="font-mono text-[10px] uppercase">Discount %</Label>
                            <Input type="number" min="0" max="100" value={it.discount_percent} onChange={(e) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, discount_percent: parseFloat(e.target.value || 0) } : x) })} />
                          </div>
                          <div>
                            <Label className="font-mono text-[10px] uppercase">Negotiated (₹) *</Label>
                            <Input data-testid={`quote-item-${idx}-price`} type="number" min="0" value={it.negotiated_price} onChange={(e) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, negotiated_price: parseFloat(e.target.value || 0) } : x) })} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <Label className="font-mono text-[10px] uppercase">GST / Tax %</Label>
                            <Input type="number" min="0" max="100" value={it.tax_percent} onChange={(e) => setForm({ ...form, items: form.items.map((x, i) => i === idx ? { ...x, tax_percent: parseFloat(e.target.value || 0) } : x) })} />
                          </div>
                          <div>
                            <Label className="font-mono text-[10px] uppercase">Line total (incl. tax)</Label>
                            <div className="h-9 flex items-center px-3 border border-border rounded-md bg-background font-mono text-sm font-bold" data-testid={`quote-item-${idx}-total`}>{INR(lineTotal(it))}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button data-testid="quote-add-item-btn" variant="outline" size="sm" className="mt-2" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })}><Plus size={12} className="mr-1" />Add line item</Button>
                </div>
                <div className="border border-border rounded-md p-4 bg-muted/30 font-mono text-sm space-y-1">
                  <div className="flex justify-between"><span>Subtotal</span><span>{INR(subtotal)}</span></div>
                  <div className="flex justify-between"><span>Tax</span><span>{INR(taxTotal)}</span></div>
                  <div className="flex justify-between border-t border-border pt-1 font-bold"><span>Total</span><span className="text-primary" data-testid="quote-form-total">{INR(finalTotal)}</span></div>
                </div>
                <div><Label className="font-mono text-[10px] uppercase">Terms and conditions</Label><Textarea rows={2} value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} /></div>
                <Button data-testid="quote-form-submit" onClick={save} className="w-full bg-primary hover:bg-primary/90">Create Quotation</Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="quotes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Quote # or client…" className="pl-9 w-56" />
        </div>
        <Select value={statusF || "__all__"} onValueChange={(v) => setStatusF(v === "__all__" ? "" : v)}>
          <SelectTrigger data-testid="quotes-status-filter" className="w-40"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={clientF || "__all__"} onValueChange={(v) => setClientF(v === "__all__" ? "" : v)}>
          <SelectTrigger className="w-52"><SelectValue placeholder="All clients" /></SelectTrigger>
          <SelectContent><SelectItem value="__all__">All clients</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">From</span><Input type="date" className="w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} /></div>
        <div className="flex items-center gap-1"><span className="font-mono text-[10px] uppercase text-muted-foreground">To</span><Input type="date" className="w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} /></div>
        {hasFilters && <Button variant="ghost" size="sm" onClick={clearFilters}>Clear</Button>}
      </div>

      {filtered.length === 0 ? <EmptyState icon={FileText} title="No quotations match" /> :
        <div className="border border-border rounded-md overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <th className="px-3 py-2.5">Quote #</th>
                <th className="px-3 py-2.5">Client</th>
                <th className="px-3 py-2.5">Date</th>
                <th className="px-3 py-2.5">Valid Until</th>
                <th className="px-3 py-2.5 text-right">Amount</th>
                <th className="px-3 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((quo) => {
                const client = clients.find((c) => c.id === quo.client_id);
                return (
                  <tr key={quo.id} className="data-row border-t border-border cursor-pointer" onClick={() => setDetail(quo)} data-testid={`quote-row-${quo.id}`}>
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-primary">{quo.quote_number}</td>
                    <td className="px-3 py-2.5">{client?.company_name || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{fmtDate(quo.date)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{fmtDate(quo.valid_until)}</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{INR(quo.final_amount)}</td>
                    <td className="px-3 py-2.5">
                      <div onClick={(e) => e.stopPropagation()}>
                        <Select value={quo.status} onValueChange={(v) => changeStatus(quo, v)}>
                          <SelectTrigger className="h-7 w-32 border-0 bg-transparent p-0 [&>svg]:hidden"><StatusBadge value={quo.status} /></SelectTrigger>
                          <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      }

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="sm:max-w-2xl">
          {detail && (() => {
            const client = clients.find((c) => c.id === detail.client_id);
            return (
              <>
                <DialogHeader>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Quotation</div>
                  <DialogTitle className="flex items-center gap-3">
                    <span className="text-primary font-mono">{detail.quote_number}</span>
                    <StatusBadge value={detail.status} />
                  </DialogTitle>
                  <div className="text-sm text-muted-foreground">{client?.company_name} · {fmtDate(detail.date)} → {fmtDate(detail.valid_until)}</div>
                </DialogHeader>

                <div className="border border-border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <th className="px-3 py-2">Service</th>
                        <th className="px-3 py-2 text-right">Qty</th>
                        <th className="px-3 py-2 text-right">Price</th>
                        <th className="px-3 py-2 text-right">GST</th>
                        <th className="px-3 py-2 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.items || []).map((it, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="px-3 py-2">
                            <div className="font-semibold">{it.service_name}</div>
                            {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                          </td>
                          <td className="px-3 py-2 text-right font-mono">{it.quantity}</td>
                          <td className="px-3 py-2 text-right font-mono">{INR(it.negotiated_price || it.original_price)}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-muted-foreground">{it.tax_percent}%</td>
                          <td className="px-3 py-2 text-right font-mono font-bold">{INR(lineTotal(it))}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-border font-mono text-sm">
                        <td colSpan={4} className="px-3 py-2 text-right text-muted-foreground">Subtotal</td>
                        <td className="px-3 py-2 text-right">{INR(detail.subtotal)}</td>
                      </tr>
                      <tr className="font-mono text-sm">
                        <td colSpan={4} className="px-3 py-2 text-right text-muted-foreground">Tax</td>
                        <td className="px-3 py-2 text-right">{INR(detail.tax_total)}</td>
                      </tr>
                      <tr className="font-mono text-base font-bold border-t border-border">
                        <td colSpan={4} className="px-3 py-2 text-right">Total</td>
                        <td className="px-3 py-2 text-right text-primary">{INR(detail.final_amount)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                {detail.terms && (<div className="text-sm"><div className="font-mono text-[10px] uppercase text-muted-foreground mb-1">Terms</div><div>{detail.terms}</div></div>)}

                <DialogFooter className="gap-2 flex-wrap">
                  <Button variant="outline" onClick={() => setDetail(null)}>Close</Button>
                  <Button data-testid="quote-detail-print" variant="outline" onClick={() => window.open(`/quotations/${detail.id}/print`, "_blank")}>
                    <Printer size={14} className="mr-1" />Print / PDF
                  </Button>
                  {detail.status !== "Accepted" && (
                    <Button data-testid="quote-detail-convert" onClick={() => convert(detail)} className="bg-primary hover:bg-primary/90">
                      Convert to project <ArrowRight size={14} className="ml-1" />
                    </Button>
                  )}
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
